import React, { useRef, useState, type FC } from 'react';
import { Dialog, Stack } from '@mui/material';
import { t } from 'i18next';
import styles from '../searchUserDialog/searchUserDialog.module.scss';
import searchIcon from '../../../../assets/search.svg';
import spinner from '../../../../assets/loader_export.svg';
import { AdminContext } from '../../../../context/AdminProvider';
import ObrasAPI from '../../../../services/APIObras';
import { Usuario } from '../../../../types/Admin';

interface SearchRegisteredUserDialogProps {
  open: boolean;
  handleOpen: (open: boolean) => void;
}

const SearchRegisteredUserDialog: FC<SearchRegisteredUserDialogProps> = ({ open, handleOpen }) => {
  const [value, setValue] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const api = ObrasAPI.getInstance();
  const adminContext = React.useContext(AdminContext);

  const handleChangeInput = (newValue: string) => {
    setValue(newValue);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (newValue.trim().length < 3) {
      setFilteredUsers([]);
      setNoResults(false);
      setLoading(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      setNoResults(false);
      try {
        const result = await api.getAllNoPhiaUsers(newValue.trim());
        const users = Array.isArray(result) ? result : [];
        setFilteredUsers(users);
        setNoResults(users.length === 0);
      } catch (error) {
        console.error('Unable to search registered AuthZ users', error);
        setFilteredUsers([]);
        setNoResults(true);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const selectUser = (user: Usuario) => {
    adminContext.handleChangeUserToEdit({ name: user.nombre, email: user.email });
    adminContext.handleChangePermissionToEdit(undefined);
    adminContext.handleChangeHasProject(false);
    adminContext.handleSetIsEdition(true);
    handleOpen(false);
  };

  return (
    <div data-testid='search-registered-user-dialog' className={styles.searchUserDialog}>
      <Dialog
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
        PaperProps={{ sx: { borderRadius: '20px' } }}
        open={open}
        onClose={() => handleOpen(false)}
      >
        <div className={styles.dialogContent}>
          <p className={styles.header}>{t('administration.searchDialog.searchUser')}</p>
          <p className={styles.text}>{t('administration.searchDialog.searchUserTip')}</p>
          <div className={styles.userInput}>
            <div className={styles.userInputTitle}>{t('administration.user')}</div>
            <input
              className={styles.inputField}
              value={value}
              onChange={(event) => handleChangeInput(event.target.value)}
              autoFocus
            />
            <img className={styles.searchIcon} src={searchIcon} />
          </div>
          <div className={styles.filteredUsers}>
            {loading && (
              <div className={styles.loaderContainer}>
                <img src={spinner} alt="loading..." width="30px" className={styles.loader} />
              </div>
            )}
            {!loading && (
              <>
                <table>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className={styles.userIdentity}>
                            <span className={styles.userName}>{user.nombre}</span>
                            <span className={styles.userEmail}>{user.email}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.addProjectContainer}>
                            <div className={styles.notIncluded} onClick={() => selectUser(user)}>
                              + {t('administration.searchDialog.addToProject')}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {noResults && <p>{t('administration.table.notFound')}</p>}
              </>
            )}
          </div>
          <Stack spacing={2} direction="row" justifyContent="center">
            <div className={styles.buttonClose} onClick={() => handleOpen(false)}>{t('administration.searchDialog.close')}</div>
          </Stack>
        </div>
      </Dialog>
    </div>
  );
};

export default SearchRegisteredUserDialog;
